import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import type { AuthContextType, AuthUser } from "./auth.types";

import { clearCurrentUserCache, getCurrentUser } from "./auth.service";
import { useSidebarStore } from "../store/sidebar.store";
import signalRService from "../services/SignalrService";
import { clearAllLocalDrafts } from "../pages/agreement/newContract/agreementDraftStorage";

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);

  const [loading, setLoading] = useState(true);

  const isAuthenticated = !!user;

  /**
   * REFRESH USER
   */
  const refreshUser = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("accessToken");

      /**
       * NO TOKEN
       */
      if (!token) {
        setUser(null);
        return;
      }

      /**
       * GET CURRENT USER
       */
      const userData = await getCurrentUser();

      setUser(userData);
    } catch (error) {
      console.error("AUTH REFRESH ERROR", error);

      setUser(null);
      clearCurrentUserCache();

      localStorage.removeItem("accessToken");

      localStorage.removeItem("refreshToken");
    } finally {
      setLoading(false);
    }
  };

  /**
   * INITIAL LOAD
   */
  useEffect(() => {
    refreshUser();
  }, []);

  /**
   * LOGIN
   */
  const login = async (token: string, refreshToken?: string) => {
    try {
      localStorage.setItem("accessToken", token);

      if (refreshToken) {
        localStorage.setItem("refreshToken", refreshToken);
      }

      // App.tsx only starts the SignalR connection once, at initial mount —
      // it now skips that if there's no token yet (e.g. loading straight to
      // /login), so a login that doesn't reload the page needs to trigger
      // the connection itself. startConnection() is a no-op if one is
      // already open, so this is safe even after a page load that already
      // connected.
      signalRService.startConnection(token);

      /**
       * LOAD USER
       */
      await refreshUser();
    } catch (error) {
      console.error("LOGIN ERROR", error);

      throw error;
    }
  };

  /**
   * LOGOUT
   */

  const logout = () => {
    const storageKeys = [
      "accessToken",
      "refreshToken",
      "organisation",
      "companyName",
      "auth-storage",
    ];
    storageKeys.forEach((key) => localStorage.removeItem(key));
    // Half-filled forms belong to the member signing out, not the next one.
    clearAllLocalDrafts();

    sessionStorage.clear();

    try {
      const cookies = document.cookie.split(";");
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i];
        const eqPos = cookie.indexOf("=");
        const name =
          eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();

        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
      }
    } catch (e) {
      console.error("Cookie clearing failed:", e);
    }

    setUser(null);
    // getCurrentUser() memoises the signed-in user for the session; drop it
    // here so nothing can read the previous account's permissions in the
    // window before the reload lands.
    clearCurrentUserCache();

    // The redirect below is a hard page reload, which would normally wipe
    // any stray open-panel state on its own — but closing it here too
    // avoids a visible flash of the mobile nav drawer still open during
    // the brief window before that reload lands.
    useSidebarStore.setState({ open: false });

    setTimeout(() => {
      window.location.replace("/login");
    }, 50);
  };
  /**
   * ACTION PERMISSION
   */
  const hasPermission = (
    module: string | number,
    action: string | number,
  ): boolean => {
    if (!user) {
      return false;
    }

    if (user.isAdmin) {
      return true;
    }

    // Number conversion safety ke liye
    const actions = user.permissions[Number(module)];

    if (!actions) {
      return false;
    }

    return actions.has(Number(action));
  };

  /**
   * MODULE VISIBILITY
   */
  const isModuleVisible = (module: string | number): boolean => {
    if (!user) {
      return false;
    }

    if (user.isAdmin) {
      return true;
    }

    const actions = user.permissions[Number(module)];

    return actions instanceof Set && actions.size > 0;
  };
  /**
   * CONTEXT VALUE
   */
  const value = useMemo(
    () => ({
      user,
      loading,
      isAuthenticated,

      login,
      logout,

      refreshUser,

      hasPermission,
      isModuleVisible,
    }),

    [user, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * AUTH HOOK
 */
export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider");
  }

  return context;
};
